import maplibregl, { type Map as MlMap, type MapGeoJSONFeature } from "maplibre-gl";
import { Protocol } from "pmtiles";
import { installFarLabels } from "./river-labels-far";
import { basemapFor, currentTheme, installTheme } from "./theme";
import {
  INITIAL_VIEW,
  RIVERS,
  RIVER_LABEL_TILES,
  OSM_WATERWAY_ATTRIBUTION,
  DISCHARGE_COLOR,
  OUTLET_COLOR,
  OUTLET_LABEL_TH,
  ATTRIBUTION,
} from "./config";

export type RiverColorMode = "discharge" | "outlet";

const RIVER_GLOW = "rivers-glow";
const RIVER_LINE = "rivers-line";

/** Line width: main rivers (ORD_STRA ≥5) ≥2.5px so they're always visible;
 *  small tributaries stay thin. One top-level zoom interpolate (MapLibre rule),
 *  main/tributary chosen per-stop with "case". (item 7a) */
const isMain: unknown = [">=", ["coalesce", ["get", "ord"], 0], 5];
const LINE_WIDTH: unknown[] = [
  "interpolate", ["linear"], ["zoom"],
  5, ["case", isMain, 2.5, ["interpolate", ["linear"], ["coalesce", ["get", "dis_av"], 0], 0, 0.5, 250, 1.6, 600, 2.4]],
  10, ["case", isMain, 3.4, ["interpolate", ["linear"], ["coalesce", ["get", "dis_av"], 0], 0, 0.9, 250, 2, 600, 3]],
  13, ["case", isMain, 5, ["interpolate", ["linear"], ["coalesce", ["get", "dis_av"], 0], 0, 1.2, 250, 2.4, 600, 3.4]],
];

/** iOS Safari computes the viewport late and changes it as the URL bar hides, so
 *  the GL canvas can init at the wrong (tiny) size. Re-run map.resize() on every
 *  signal that the viewport changed: load, ResizeObserver, orientation, the
 *  visual viewport, a custom "fs-resize" event (drawer/sheet), and a few delayed
 *  ticks after first paint. */
function setupMapResize(map: MlMap, container: HTMLElement): void {
  let raf = 0;
  const resize = (): void => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => map.resize()); };
  map.once("load", resize);
  for (const ms of [60, 250, 600, 1200]) setTimeout(resize, ms);
  try { new ResizeObserver(resize).observe(container); } catch { /* old browsers */ }
  window.addEventListener("orientationchange", () => setTimeout(resize, 250));
  window.addEventListener("resize", resize);
  window.addEventListener("fs-resize", resize);
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", resize);
    window.visualViewport.addEventListener("scroll", resize);
  }
}

/** Ask the map to re-measure (dispatch from UI when the layout changes). */
export function requestMapResize(): void { window.dispatchEvent(new Event("fs-resize")); }

export function createMap(container: HTMLElement): MlMap {
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);

  const map = new maplibregl.Map({
    container,
    style: basemapFor(currentTheme()),
    center: INITIAL_VIEW.center,
    zoom: INITIAL_VIEW.zoom,
    minZoom: INITIAL_VIEW.minZoom,
    maxZoom: INITIAL_VIEW.maxZoom,
    attributionControl: false,
    hash: false,
    dragRotate: false,
    pitchWithRotate: false,
  });

  installTheme(map); // before any of our layers: their colours follow the light / dark mode (v0.9.9 B)
  map.addControl(new maplibregl.AttributionControl({ compact: false, customAttribution: ATTRIBUTION }), "bottom-right");
  // zoom buttons only on desktop — on mobile the top-left is the ☰ menu and pinch
  // zoom is available (avoids the ☰/＋ overlap seen on iOS).
  if (!window.matchMedia("(max-width: 767px)").matches) {
    map.addControl(new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }), "top-left");
  }
  map.touchZoomRotate.disableRotation();

  setupMapResize(map, container);
  return map;
}

/** Add the rivers vector layers (glow + line) from the pmtiles archive. */
export function addRiversLayer(map: MlMap): void {
  map.addSource("rivers", {
    type: "vector",
    url: `pmtiles://${location.origin}${RIVERS.pmtilesPath}`,
    attribution: ATTRIBUTION,
  });

  map.addLayer({
    id: RIVER_GLOW,
    type: "line",
    source: "rivers",
    "source-layer": RIVERS.sourceLayer,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": DISCHARGE_COLOR as never,
      "line-width": LINE_WIDTH as never,
      "line-opacity": 0.35,
      "line-blur": 6,
    },
  });

  map.addLayer({
    id: RIVER_LINE,
    type: "line",
    source: "rivers",
    "source-layer": RIVERS.sourceLayer,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": DISCHARGE_COLOR as never,
      "line-width": LINE_WIDTH as never,
      "line-opacity": 0.95,
    },
  });
}

const RIVER_LABELS = "river-labels";

/** River / canal NAME labels (v0.9.5c) from river-labels.pmtiles (preprocess/build_river_labels.mjs):
 *  HydroRIVERS lines named with the CARD's rule (mainRiverName) so map = card, tiered from the
 *  data (discharge + length → z4+ national rivers · z7+ secondary · z10+ main canals by OSM
 *  length). Added right above the river lines and BELOW every later layer (dams, C.13, staffs,
 *  POPNIX, route) → those win placement and river names hide on collision.
 *  (Before v0.9.5c no name showed at all: the OSM layer asked OpenFreeMap for the font stack
 *   "Open Sans Regular,Noto Sans Regular" → 404 since the v0.8.0 basemap switch.) */
export function addRiverLabelsLayer(map: MlMap): void {
  if (map.getSource(RIVER_LABELS)) return;
  map.addSource(RIVER_LABELS, {
    type: "vector",
    url: `pmtiles://${location.origin}${RIVER_LABEL_TILES.pmtilesPath}`,
    attribution: OSM_WATERWAY_ATTRIBUTION,
  });
  const paint = {
    "text-color": ["match", ["get", "tier"], 4, "#a9dcff", "#e2f1ff"],
    "text-halo-color": "#05080f",
    "text-halo-width": 1.6,
    "text-opacity": 0.95,
  } as NonNullable<maplibregl.SymbolLayerSpecification["paint"]>;
  // bigger rivers are placed first (tier, then the data's mean discharge)
  const sortKey: maplibregl.ExpressionSpecification = ["-", ["*", ["get", "tier"], 100000], ["get", "dis"]];
  // z4–6: one point label per national river, placed at run time clear of dams / staffs / pin
  // below the dams when they are already there (dam names outrank river names — v0.9.10a)
  void installFarLabels(map, map.getLayer("dams-layer") ? "dams-layer" : undefined, paint);
  // z7+: names along the river line
  map.addLayer({
    id: RIVER_LABELS, type: "symbol", source: RIVER_LABELS, "source-layer": RIVER_LABEL_TILES.sourceLayer,
    minzoom: 7, filter: ["==", ["geometry-type"], "LineString"] as maplibregl.FilterSpecification,
    layout: {
      "symbol-placement": "line",
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"], // the only stack OpenFreeMap serves
      "text-size": ["match", ["get", "tier"], 1, 13, 2, 12, 11],
      "text-max-angle": 60,
      // sparse at the regional view; from z10 (city, staffs / gauges everywhere) more anchors so the name
      // still finds a free spot between the symbols that outrank it
      "symbol-spacing": ["step", ["zoom"], 300, 10, 150],
      "symbol-sort-key": sortKey,
      "text-allow-overlap": false,
      "text-padding": 6,
    },
    paint,
  });
}

export function setRiverColorMode(map: MlMap, mode: RiverColorMode): void {
  const expr = (mode === "outlet" ? OUTLET_COLOR : DISCHARGE_COLOR) as never;
  for (const id of [RIVER_GLOW, RIVER_LINE]) {
    if (map.getLayer(id)) map.setPaintProperty(id, "line-color", expr);
  }
}

export function setRiversVisible(map: MlMap, visible: boolean): void {
  const v = visible ? "visible" : "none";
  for (const id of [RIVER_GLOW, RIVER_LINE]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  }
}

/** Wire click → popup and hover cursor on the rivers line layer. */
export function enableRiverInteraction(map: MlMap): void {
  map.on("mouseenter", RIVER_LINE, () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", RIVER_LINE, () => {
    map.getCanvas().style.cursor = "";
  });
  map.on("click", RIVER_LINE, (e) => {
    const f = e.features?.[0];
    if (!f) return;
    new maplibregl.Popup({ closeButton: true, maxWidth: "260px", className: "fs-popup" })
      .setLngLat(e.lngLat)
      .setHTML(riverPopupHtml(f))
      .addTo(map);
  });
}

function riverPopupHtml(f: MapGeoJSONFeature): string {
  const p = f.properties ?? {};
  const outlet = String(p["outlet"] ?? "other");
  const toGulf = outlet === "gulf";
  const dis = Number(p["dis_av"] ?? 0);
  const ord = p["ord"] ?? "—";
  const dest = OUTLET_LABEL_TH[outlet] ?? outlet;
  const flow = toGulf
    ? `<span class="pop-gulf">ไหลลงอ่าวไทย ✓</span>`
    : `<span class="pop-other">ไม่ไหลลงอ่าวไทย (ไหลลง${dest})</span>`;
  return `
    <div class="pop">
      <div class="pop-row">${flow}</div>
      <div class="pop-grid">
        <span>น้ำท่าเฉลี่ย</span><b>${dis.toLocaleString("en-US", { maximumFractionDigits: 1 })} ลบ.ม./วิ</b>
        <span>ลำดับลำน้ำ</span><b>Strahler ${ord}</b>
        <span>รหัสช่วงลำน้ำ</span><b>${p["id"] ?? "—"}</b>
      </div>
    </div>`;
}
